import React, { createContext, useContext } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Create a context to pass drag handle listener props down to child components
const DragHandleContext = createContext<{
  listeners: Record<string, any> | null;
  attributes: Record<string, any> | null;
}>({
  listeners: null,
  attributes: null
});

export const useDragHandle = () => useContext(DragHandleContext);

// Drag handle component that consumes the context
export function DragHandle({ children }: { children: React.ReactNode }) {
  const { attributes, listeners } = useDragHandle();
  
  return (
    <div {...attributes} {...listeners} className="cursor-grab">
      {children}
    </div>
  );
}

interface SortableItemProps {
  id: string;
  children: React.ReactNode | (({ isDragging }: { isDragging: boolean }) => React.ReactNode);
  handleSelector?: string;
}

export function SortableItem({ id, children, handleSelector }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id,
    ...(handleSelector ? { handle: true } : {})
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : 'auto',
    position: 'relative' as const,
  };

  // If using a handle, provide listeners through context
  if (handleSelector) {
    return (
      <DragHandleContext.Provider value={{ attributes, listeners: listeners || null }}>
        <div
          ref={setNodeRef}
          style={style}
          data-draggable-item
          data-id={id}
          className={isDragging ? 'opacity-50' : ''}
        >
          {typeof children === 'function' 
            ? children({ isDragging }) 
            : children}
        </div>
      </DragHandleContext.Provider>
    );
  }

  // When not using a handle, apply listeners directly to the container
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      data-draggable-item
      data-id={id}
      className={isDragging ? 'opacity-50' : ''}
    >
      {typeof children === 'function' 
        ? children({ isDragging }) 
        : children}
    </div>
  );
}